import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

interface StatProps {
  value: string;
  label: string;
  delay: number;
}

function AnimatedStat({ value, label, delay }: StatProps) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), delay);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className={`text-center transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
    >
      <p className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-gold mb-2 tracking-tight">
        {value}
      </p>
      <p className="text-sm md:text-base text-charcoal-200 font-sans leading-relaxed max-w-[200px] mx-auto">
        {label}
      </p>
    </div>
  );
}

export default function IowaWaterCrisisPage() {
  const [reveal, setReveal] = useState(false);
  const [showRefs, setShowRefs] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setReveal(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { value: "2x", label: "The increased risk of certain cancers, such as ovarian cancer, observed in Iowa women exposed to elevated nitrate levels in public drinking water.", delay: 100 },
    { value: "2nd", label: "Iowa's national rank for overall cancer incidence, prompting ongoing public health research.", delay: 250 },
    { value: "10 mg/L", label: "The EPA's current legal limit for nitrates—a standard originally set in 1962.", delay: 400 },
    { value: "$1.5M", label: "The historical annual cost for Des Moines Water Works to strip nitrates from the municipal supply.", delay: 550 },
  ];

  const references = [
    {
      title: "Inoue-Choi et al. (2014) — Nitrate and Ovarian Cancer Risk",
      url: "https://doi.org/10.1002/ijc.29365",
      description: "Analysis of the Iowa Women's Health Study finding 2.03x increased ovarian cancer risk among postmenopausal women with elevated nitrate exposure (≥2.98 mg/L) in public drinking water. International Journal of Cancer, 137, 173–182.",
    },
    {
      title: "Weyer et al. (2001) — Municipal Drinking Water Nitrate and Cancer Risk",
      url: "https://doi.org/10.1097/00001648-200105000-00013",
      description: "Iowa Women's Health Study analysis of 21,000+ women finding 2.83x relative risk for bladder cancer in the highest quartile of nitrate exposure from municipal water supplies. Epidemiology, 12, 327–338.",
    },
    {
      title: "Iowa Cancer Registry Annual Report",
      url: "https://cancerregistry.iowa.gov/",
      description: "Statewide cancer surveillance data documenting cancer incidence rates across Iowa counties.",
    },
    {
      title: "Center for Health Effects of Environmental Contamination (CHEEC)",
      url: "https://cheec.uiowa.edu/",
      description: "University of Iowa research center studying the correlation between environmental contaminants and public health outcomes.",
    },
    {
      title: "Environmental Protection Agency — Safe Drinking Water Guidelines",
      url: "https://www.epa.gov/ground-water-and-drinking-water/national-primary-drinking-water-regulations",
      description: "EPA federal standard for nitrate in public drinking water systems at 10 mg/L, originally established in 1962.",
    },
    {
      title: "Des Moines Water Works — Nitrate Treatment & 2015 Litigation",
      url: "https://www.desmoineswaterworks.org/",
      description: "Information on the 2015 lawsuit against upstream counties and the ongoing operational challenges of nitrate removal.",
    },
    {
      title: "Central Iowa Source Water Resource Assessment (CISWRA)",
      url: "https://www.iowadnr.gov/",
      description: "Regional assessment of source water quality in central Iowa, including agricultural runoff impact data.",
    },
  ];

  return (
    <div className="min-h-screen bg-dark-bg">
      <Navbar />
      <main>
        {/* Header */}
        <section className="pt-28 md:pt-36 pb-12 md:pb-16 bg-dark-bg">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-charcoal-400 hover:text-gold transition-colors duration-300 font-sans mb-6"
            >
              <i className="ri-arrow-left-line" />
              Back to Home
            </Link>
            <p className="text-xs md:text-sm font-sans font-semibold tracking-[0.2em] text-gold uppercase mb-4">
              Research & Data
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium text-charcoal-50 leading-tight tracking-tight-xl mb-6">
              The Iowa Water Crisis
            </h1>
            <p className="text-base md:text-lg text-charcoal-300 font-sans leading-relaxed max-w-2xl">
              Decades of agricultural runoff have impacted Iowa's groundwater, making water quality a top priority for families. Here is what you need to know about nitrates, ongoing environmental research, and the most effective steps to reduce contaminants in your home.
            </p>
          </div>
        </section>

        <section ref={sectionRef} className="pb-24 md:pb-32 bg-dark-bg">
          <div className="w-full px-6 md:px-10 lg:px-16">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-20 md:mb-24">
              {stats.map((stat) => (
                <AnimatedStat
                  key={stat.label}
                  value={stat.value}
                  label={stat.label}
                  delay={stat.delay}
                />
              ))}
            </div>

            {/* Full Content Cards */}
            <div
              className={`flex flex-col gap-6 md:gap-8 transition-all duration-700 delay-300 ${
                reveal ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {/* Card 1 */}
                <div className="bg-dark-surface border border-dark-border rounded-xl p-6 md:p-8">
                  <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mb-5 border border-gold/20">
                    <i className="ri-drop-line text-gold text-xl" />
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl font-semibold text-charcoal-50 mb-3">
                    The Nitrate Problem
                  </h3>
                  <p className="text-sm md:text-base text-charcoal-300 font-sans leading-relaxed mb-4">
                    Iowa's agricultural dominance is central to our economy, but it creates complex challenges for our water supply. Fertilizer runoff introduces nitrates into the groundwater, which municipal systems and private wells must constantly manage. The EPA's legal limit is 10 mg/L—a threshold established over 60 years ago primarily to prevent infant "blue baby syndrome."
                  </p>
                  <p className="text-sm md:text-base text-charcoal-300 font-sans leading-relaxed mb-4">
                    However, environmental experts point out that the 10 mg/L limit was not designed to address long-term adult exposure. Recent peer-reviewed research highlights that even water meeting these baseline legal standards may contain agricultural byproducts at levels that warrant caution. For many families in the Greater DSM Area, merely meeting a 1960s standard is not enough.
                  </p>
                  <p className="text-sm md:text-base text-charcoal-300 font-sans leading-relaxed">
                    Private wells are particularly vulnerable, as they receive no municipal treatment and can experience nitrate spikes after spring rainfall and heavy fertilizer application cycles. Seasonal testing often reveals levels that fluctuate dramatically throughout the year, leaving families unaware of what is actually flowing from their taps during peak agricultural periods.
                  </p>
                </div>

                {/* Card 2 */}
                <div className="bg-dark-surface border border-dark-border rounded-xl p-6 md:p-8">
                  <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mb-5 border border-gold/20">
                    <i className="ri-heart-pulse-line text-gold text-xl" />
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl font-semibold text-charcoal-50 mb-3">
                    Public Health and Ongoing Research
                  </h3>
                  <p className="text-sm md:text-base text-charcoal-300 font-sans leading-relaxed mb-4">
                    According to the Iowa Cancer Registry, Iowa has the second-highest cancer incidence rate in the country. While cancer is complex and multi-causal, researchers at institutions like the Center for Health Effects of Environmental Contamination (CHEEC) are actively studying the correlation between agricultural states, heavy nitrate loads, and public health.
                  </p>
                  <p className="text-sm md:text-base text-charcoal-300 font-sans leading-relaxed mb-4">
                    The <em>Iowa Women's Health Study</em>, one of the most widely cited epidemiological studies on drinking water and cancer risk, has yielded significant findings specific to our state. An analysis of postmenopausal women found that those exposed to the highest levels of nitrates in public drinking water (&ge;2.98 mg/L) had a 2.03 times higher risk of developing ovarian cancer compared to those with the lowest exposure&mdash;even at levels well below the EPA's 10 mg/L limit.
                  </p>
                  <p className="text-sm md:text-base text-charcoal-300 font-sans leading-relaxed">
                    An earlier study of over 21,000 Iowa women found the relative risk for bladder cancer jumped to 2.83 for those in the highest quartile of nitrate exposure. While municipal water providers work hard to meet EPA guidelines, a growing body of evidence suggests that nitrate levels far below the 10 mg/L standard may warrant caution. The risk requires proactive management, not panic.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {/* Card 3 */}
                <div className="bg-dark-surface border border-dark-border rounded-xl p-6 md:p-8">
                  <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mb-5 border border-gold/20">
                    <i className="ri-government-line text-gold text-xl" />
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl font-semibold text-charcoal-50 mb-3">
                    The Municipal Challenge
                  </h3>
                  <p className="text-sm md:text-base text-charcoal-300 font-sans leading-relaxed mb-4">
                    In 2015, Des Moines Water Works initiated a landmark lawsuit against upstream counties to address the sheer volume of nitrates entering the water supply. While the lawsuit was dismissed, it illuminated the massive financial and operational burden placed on municipal water treatment facilities.
                  </p>
                  <p className="text-sm md:text-base text-charcoal-300 font-sans leading-relaxed">
                    Municipal systems successfully treat millions of gallons daily to meet baseline safety codes, but managing fluctuating seasonal nitrate spikes from heavy rainfall and agricultural runoff remains an ongoing, expensive challenge. The infrastructure is working overtime, but the source problem remains.
                  </p>
                </div>

                {/* Card 4 */}
                <div className="bg-dark-surface border border-dark-border rounded-xl p-6 md:p-8">
                  <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mb-5 border border-gold/20">
                    <i className="ri-shield-check-line text-gold text-xl" />
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl font-semibold text-charcoal-50 mb-3">
                    What You Can Do
                  </h3>
                  <p className="text-sm md:text-base text-charcoal-300 font-sans leading-relaxed mb-4">
                    Large-scale municipal treatment provides a baseline of safety, but point-of-use filtration is one of the most effective ways to take ultimate control of your tap water. Whole-house and under-sink filtration systems provide an essential final layer of protection, dramatically reducing nitrates, heavy metals, and pesticides before they reach your family's glasses.
                  </p>
                  <p className="text-sm md:text-base text-charcoal-300 font-sans leading-relaxed">
                    Our systems are engineered specifically for Iowa's unique water profile. Investing in advanced home filtration gives you direct control over your water quality—because waiting for seasonal municipal fluctuations to stabilize is not a strategy.
                  </p>
                </div>
              </div>
            </div>

            {/* References Accordion */}
            <div
              className={`mt-16 md:mt-20 transition-all duration-700 delay-500 ${
                reveal ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <button
                onClick={() => setShowRefs(!showRefs)}
                className="flex items-center gap-3 group cursor-pointer"
              >
                <div className="w-8 h-8 rounded-full bg-dark-surface border border-dark-border flex items-center justify-center group-hover:border-gold/40 transition-colors duration-300">
                  <i
                    className={`ri-arrow-down-s-line text-gold transition-transform duration-300 ${
                      showRefs ? "rotate-180" : ""
                    }`}
                  />
                </div>
                <span className="text-sm font-sans font-semibold tracking-wide text-charcoal-400 group-hover:text-gold transition-colors duration-300 uppercase">
                  References & Sources
                </span>
              </button>

              <div
                className={`overflow-hidden transition-all duration-500 ${
                  showRefs ? "max-h-[1200px] opacity-100 mt-6" : "max-h-0 opacity-0 mt-0"
                }`}
              >
                <div className="bg-dark-surface border border-dark-border rounded-xl p-6 md:p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {references.map((ref, index) => (
                      <div key={index} className="flex gap-3">
                        <span className="text-xs font-sans font-semibold text-gold mt-1 flex-shrink-0">
                          [{index + 1}]
                        </span>
                        <div>
                          <a
                            href={ref.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              e.preventDefault();
                              window.open(ref.url, '_blank', 'noopener,noreferrer');
                            }}
                            className="text-sm font-sans font-medium text-charcoal-200 hover:text-gold transition-colors duration-300 leading-snug cursor-pointer"
                          >
                            {ref.title}
                          </a>
                          <p className="text-xs text-charcoal-400 font-sans mt-1 leading-relaxed">
                            {ref.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div
              className={`mt-12 md:mt-16 transition-all duration-700 delay-500 ${
                reveal ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <p className="text-xs text-charcoal-400 font-sans leading-relaxed text-center max-w-3xl mx-auto">
                <em>Disclaimer: Our filtration systems are designed to reduce specific contaminants from drinking water. They are not intended to diagnose, treat, cure, or prevent any disease.</em>
              </p>
            </div>

            {/* Bottom CTA */}
            <div
              className={`mt-16 md:mt-20 text-center transition-all duration-700 delay-500 ${
                reveal ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <p className="font-serif text-xl md:text-2xl text-charcoal-50 mb-6 leading-relaxed">
                Every Iowan deserves clean water.
                <br />
                <span className="text-gold">That includes you.</span>
              </p>
              <Link
                to="/packages"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-dark-bg text-sm font-sans font-semibold rounded-full hover:bg-gold-light transition-colors duration-300 whitespace-nowrap"
              >
                Explore Our Systems
                <i className="ri-arrow-right-line text-xs" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}